import React, { useState } from "react";
import { Download, FileSpreadsheet, MapPin, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";

async function downloadXlsx(url, params) {
  const { data, headers } = await api.get(url, { params, responseType: "blob" });
  const name = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1] || "export.xlsx";
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: name });
  a.click(); URL.revokeObjectURL(a.href);
}

function Card({ icon: Icon, title, desc, children, testid }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600/15 text-emerald-700"><Icon className="h-5 w-5" /></div>
        <div><p className="font-display font-bold text-foreground">{title}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
      </div>
      {children}
    </section>
  );
}

const iso = (d) => d.toISOString().slice(0, 10);

export default function ExcelExport() {
  const { t } = useLang();
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [busy, setBusy] = useState("");

  const run = async (key, url, params) => {
    setBusy(key);
    try { await downloadXlsx(url, params); toast.success(t("xls_done")); }
    catch (e) {
      let msg = e.message;
      try { msg = apiError(JSON.parse(await e.response?.data?.text())?.detail); } catch { /* blob zonder json */ }
      toast.error(msg);
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="excel-export">
      <p className="text-sm text-muted-foreground flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" />{t("xls_desc")}</p>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card icon={MapPin} title={t("xls_pitches")} desc={t("xls_pitches_desc")} testid="xls-pitches-card">
          <Button onClick={() => run("pitches", "/interfaces/export/pitches")} disabled={busy === "pitches"} data-testid="xls-pitches-download">
            <Download className="h-4 w-4 mr-1.5" />{t("xls_download")}
          </Button>
        </Card>
        <Card icon={Wallet} title={t("xls_finance")} desc={t("xls_finance_desc")} testid="xls-finance-card">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">{t("from")}</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="font-mono" data-testid="xls-from" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("to")}</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="font-mono" data-testid="xls-to" /></div>
          </div>
          <Button onClick={() => run("finance", "/interfaces/export/finance", { from, to })} disabled={busy === "finance" || !from || !to || from > to} data-testid="xls-finance-download">
            <Download className="h-4 w-4 mr-1.5" />{t("xls_download")}
          </Button>
        </Card>
      </div>
    </div>
  );
}
